import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useSnackBar } from "src/components/Reusable/CustomSnackbarProvider";
import { useRealmApp } from "src/store/RealmApp";
import { settingsKeys } from "../General/settings";
import { phaseKeys } from "../Services/SharedSpace/phases";
import { useAccessToken } from "../useAccessToken";

const timesheetsKeys = {
  timesheet: (week: string, userId: string) => ["timesheet", week, userId],
  timesheets: (week: string) => ["timesheet", week],
  calendarEvents: (week: string, userId: string) => [
    "calendarEvents",
    week,
    userId,
  ],
  userAppsData: () => ["userAppsData"],
  timesheetEntriesForUser: (week: string, userId: string) => [
    "timesheet",
    "entries",
    week,
    userId,
  ],
  timesheetsForApproval: () => ["timesheet", "approval"],
  entries: (filter: any) => ["entries", filter],
};

export type TimesheetData = {
  _id: string;
  week: string;
  status: "not-submitted" | "submitted" | "approved" | "rejected";
  submittedAt?: string;
  isApproved: boolean;
  isSubmitted: boolean;
  approvedAt?: string;
  rejectedAt?: string;
  approverUsers?: {
    _id: string;
    name: string;
    email: string;
  }[];
  rows: TimesheetRowData[];
  createdAt: string;
  updatedAt: string;
};

export type TimesheetRowData = {
  _id: string;
  project: {
    _id: string;
    name: string;
  };
  item: TimesheetItemData;
  entries: TimesheetEntryData[];
  createdAt: string;
  updatedAt: string;
};

export type TimesheetItemData = {
  _id?: string;
  type: "task" | "stage" | "activity";
  name?: string;
};

export type TimesheetEntryData = {
  _id: string;
  date: string;
  time: number;
  isBillable: boolean;
  category: {
    _id: string;
    name: string;
  };
  description: string;
  createdAt: string;
  updatedAt: string;
};

export type CalendarEventData = {
  id: string;
  title: string;
  summary: string;
  start: string;
  end: string;
  duration: number;
};

export type CapacityData = {
  value: number;
  effectiveFrom: string; // "YYYY-MM-DD"
  createdAt: string;
  source: "user" | "instance";
  changedBy: {
    _id: string;
    name: string;
    email: string;
  };
};

export type CapacityTimelineData = {
  value: number;
  startDate: string;
  endDate: string | null;
  source: "user" | "instance";
};

export type BillingRateData = {
  value: number;
  effectiveFrom: string; // "YYYY-MM-DD"
  createdAt: string;
  currency: string;
  changedBy: {
    _id: string;
    name: string;
    email: string;
  };
};

export type BillingRateTimelineData = {
  value: number;
  currency: string;
  startDate: string;
  endDate: string | null;
};

export type CostRateData = {
  value: number;
  effectiveFrom: string; // "YYYY-MM-DD"
  createdAt: string;
  currency: string;
  changedBy: {
    _id: string;
    name: string;
    email: string;
  };
};

export type CostRateTimelineData = {
  value: number;
  currency: string;
  startDate: string;
  endDate: string | null;
};

export type UserAppsData = {
  _id: string;
  name: string;
  email: string;
  timeTracking: {
    capacities: CapacityData[];
    capacityTimeline: CapacityTimelineData[];
    billingRates: BillingRateData[];
    billingRateTimeline: BillingRateTimelineData[];
    costRates: CostRateData[];
    costRateTimeline: CostRateTimelineData[];
  };
};

export const useGetUserCapacities = () => {
  const app = useRealmApp();
  const getValidAccessToken = useAccessToken();
  return useQuery(
    timesheetsKeys.userAppsData(),
    async (): Promise<UserAppsData[]> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/userCapacities`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          params: {
            instanceId: app.currentUser?.customData.instanceId.$oid,
          },
        }
      );
      return res.data;
    },
    {
      staleTime: Infinity,
    }
  );
};

export const useUpdateUserCapacity = () => {
  const app = useRealmApp();
  const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();
  return useMutation({
    mutationFn: async (update: {
      userId: string;
      capacity: number;
      effectiveFrom: string;
    }): Promise<{ message: string; id: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.post(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/updateUserCapacity`,
        update,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return res.data;
    },
    onError: () => {
      snackbarCtx.showSnackbar("Error updating instance capacity", "error");
    },
    onSuccess: () => {
      queryClient.invalidateQueries(settingsKeys.settings());
      queryClient.invalidateQueries(timesheetsKeys.userAppsData());
    },
  });
};

export const useUpdateUserBillingRate = () => {
  const app = useRealmApp();
  const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();
  return useMutation({
    mutationFn: async (update: {
      userId: string;
      billingRate: {
        value: number;
        currency: string;
      };
      effectiveFrom: string;
    }): Promise<{ message: string; id: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.post(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/updateUserBillingRate`,
        update,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return res.data;
    },
    onError: () => {
      snackbarCtx.showSnackbar("Error updating instance capacity", "error");
    },
    onSuccess: () => {
      queryClient.invalidateQueries(settingsKeys.settings());
      queryClient.invalidateQueries(timesheetsKeys.userAppsData());
    },
  });
};

export const useUpdateUserCostRate = () => {
  const app = useRealmApp();
  const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();
  return useMutation({
    mutationFn: async (update: {
      userId: string;
      costRate: {
        value: number;
        currency: string;
      };
      effectiveFrom: string;
    }): Promise<{ message: string; id: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.post(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/updateUserCostRate`,
        update,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return res.data;
    },
    onError: () => {
      snackbarCtx.showSnackbar("Error updating user cost rate", "error");
    },
    onSuccess: () => {
      queryClient.invalidateQueries(settingsKeys.settings());
      queryClient.invalidateQueries(timesheetsKeys.userAppsData());
    },
  });
};

export const useUpdateInstanceCapacity = () => {
  const app = useRealmApp();
  const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();
  return useMutation({
    mutationFn: async (capacity: {
      defaultCapacity: number;
      effectiveFrom: string;
    }): Promise<{ message: string; id: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.post(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/updateInstanceCapacity`,
        {
          defaultCapacity: capacity.defaultCapacity,
          effectiveFrom: capacity.effectiveFrom,
          instanceId: app.currentUser?.customData.instanceId.$oid,
        },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return res.data;
    },
    onError: () => {
      snackbarCtx.showSnackbar("Error updating instance capacity", "error");
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(settingsKeys.settings());
      queryClient.invalidateQueries(timesheetsKeys.userAppsData());
    },
  });
};

export const useGetCalendarEvents = (week: string) => {
  const app = useRealmApp();
  const getValidAccessToken = useAccessToken();
  return useQuery(
    timesheetsKeys.calendarEvents(week, app.currentUser?.customData._id),
    async (): Promise<CalendarEventData[]> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/calendarEvents`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          params: {
            date: week,
          },
        }
      );
      return res.data;
    },
    {
      staleTime: Infinity,
    }
  );
};

export type TimeEntryData = {
  _id: string;
  description: string;
  date: string;
  time: number;
  isBillable: boolean;
  // isApproved: boolean;
  // status: "pending" | "approved" | "rejected";
  approvedAt?: string;
  rejectedAt?: string;
  project: {
    _id: string;
    name: string;
  };
  stage?: {
    _id: string;
    name: string;
  };
  task?: {
    _id: string;
    title: string;
  };
  user?: {
    _id: string;
    name: string;
    email: string;
  };
  comments: {
    user: {
      _id: string;
      name: string;
      email: string;
    };
    text: string;
    createdAt: string;
  }[];
  createdAt: string;
  updatedAt: string;
};

export type TimesheetSummaryData = {
  _id: string;
  name: string;
  email: string;
  entries: {
    Mon: { totalTime: number; totalBilledTime: number; capacity: number };
    Tue: { totalTime: number; totalBilledTime: number; capacity: number };
    Wed: { totalTime: number; totalBilledTime: number; capacity: number };
    Thu: { totalTime: number; totalBilledTime: number; capacity: number };
    Fri: { totalTime: number; totalBilledTime: number; capacity: number };
    Sat: { totalTime: number; totalBilledTime: number; capacity: number };
    Sun: { totalTime: number; totalBilledTime: number; capacity: number };
  };
};

export type TimesheetApprovalData = {
  _id: string;
  week: string;
  status: "submitted" | "approved";
  submittedAt: string;
  isApproved: boolean;
  isSubmitted: boolean;
  user: {
    _id: string;
    name: string;
    email: string;
  };
  approvedAt?: string;
  entries: TimeEntryData[];
  createdAt: string;
  updatedAt: string;
};

export const useGetTimesheetsForApproval = () => {
  const app = useRealmApp();
  const getValidAccessToken = useAccessToken();
  return useQuery(
    timesheetsKeys.timesheetsForApproval(),
    async (): Promise<TimesheetApprovalData[]> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/timesheetsForApproval`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          // params: {
          //   instanceId: app.currentUser.customData.instanceId.$oid,
          //   week,
          // },
        }
      );

      return res.data;
    },
    {
      staleTime: Infinity,
    }
  );
};
export const useGetTimesheetEntriesForWeek = (week: string) => {
  const app = useRealmApp();
  const getValidAccessToken = useAccessToken();
  return useQuery(
    timesheetsKeys.timesheets(week),
    async (): Promise<TimesheetSummaryData[]> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/timesheetEntriesForWeek`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          params: {
            instanceId: app.currentUser.customData.instanceId.$oid,
            week,
          },
        }
      );

      return res.data;
    },
    {
      staleTime: Infinity,
    }
  );
};

export const useGetTimesheetEntriesForUser = (week: string) => {
  const app = useRealmApp();
  const getValidAccessToken = useAccessToken();
  return useQuery(
    timesheetsKeys.timesheetEntriesForUser(
      week,
      app.currentUser?.customData._id
    ),
    async (): Promise<TimeEntryData[]> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/timesheetEntriesForUser`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          params: {
            userId: app.currentUser?.customData._id,
            week,
          },
        }
      );

      return res.data;
    },
    {
      staleTime: Infinity,
    }
  );
};

export const useGetEntries = (projectId: string) => {
  const app = useRealmApp();
  const filter = {
    logic: "AND",
    children: [
      {
        field: "project",
        comparator: "in",
        value: [projectId],
      },
    ],
  };
  const getValidAccessToken = useAccessToken();
  return useQuery(
    timesheetsKeys.entries(filter),
    async (): Promise<TimeEntryData[]> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/timesheetEntriesWithFilters`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          params: {
            instanceId: app.currentUser?.customData.instanceId.$oid,
            filter: JSON.stringify(filter),
          },
        }
      );

      return res.data;
    },
    {
      staleTime: Infinity,
    }
  );
};

export const useOldGetTimesheet = (week: string) => {
  const app = useRealmApp();
  const getValidAccessToken = useAccessToken();
  return useQuery(
    timesheetsKeys.timesheet(week, app.currentUser?.customData._id),
    async (): Promise<
      | {
          exists: true;
          timesheet: TimesheetData;
        }
      | {
          exists: false;
        }
    > => {
      const accessToken = await getValidAccessToken();

      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/timesheetForUserOld`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          params: {
            week,
          },
        }
      );
      if (res.status === 200) {
        return {
          exists: true,
          timesheet: res.data,
        };
      } else if (res.status === 204) {
        return {
          exists: false,
        };
      } else {
        return res.data;
      }
    },
    {
      // staleTime: Infinity,
    }
  );
};

export const useGetTimesheet = (week: string) => {
  const app = useRealmApp();
  const getValidAccessToken = useAccessToken();
  return useQuery(
    timesheetsKeys.timesheet(week, app.currentUser?.customData._id),
    async (): Promise<
      | {
          exists: true;
          timesheet: TimesheetData;
        }
      | {
          exists: false;
        }
    > => {
      const accessToken = await getValidAccessToken();

      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/timesheetForUser`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          params: {
            week,
          },
        }
      );
      if (res.status === 200) {
        return {
          exists: true,
          timesheet: res.data,
        };
      } else if (res.status === 204) {
        return {
          exists: false,
        };
      } else {
        return res.data;
      }
    },
    {
      // staleTime: Infinity,
    }
  );
};

export const useCreateTimesheetRows = (week: string) => {
  const app = useRealmApp();
  const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();
  return useMutation({
    mutationFn: async (
      timesheetRows: {
        week: string;
        phaseId: string;
        item: {
          type: "task" | "stage" | "activity";
          _id?: string;
          name?: string;
        };
        entries: {
          date: string; // "YYYY-MM-DD"
          time: number; // ms
          isBillable: boolean;
          description: string;
          categoryId?: string;
        }[];
      }[]
    ): Promise<{ message: string; id: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.post(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/timesheetRows`,
        timesheetRows,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return res.data;
    },
    onError: () => {
      snackbarCtx.showSnackbar("Error creating time entries", "error");
    },
    onSuccess: (data, variables) => {
      // navigate(`/spaces/${data.id}/tasks`);
      // queryClient.invalidateQueries(spacesKeys.spaces());
      // queryClient.invalidateQueries(
      //   statusGroupKeys.statusGroups(
      //     app.currentUser?.customData?.instanceId?.$oid
      //   )
      // );
      queryClient.invalidateQueries(
        timesheetsKeys.timesheet(week, app.currentUser?.customData._id)
      );
      const phaseIds = [...new Set(variables.map((row) => row.phaseId))];
      phaseIds.forEach((phaseId) => {
        queryClient.invalidateQueries(phaseKeys.phase(phaseId));
      });
    },
  });
};

type TimeEntryRequestParams = {
  projectId: string;
  stageId?: string;
  taskId?: string;
  date: string;
  time: number;
  isBillable: boolean;
  description: string;
  categoryId?: string[];
  userId: string;
};

export const useCreateTimeEntry = () => {
  const app = useRealmApp();
  const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();
  return useMutation({
    mutationFn: async (
      params: TimeEntryRequestParams
    ): Promise<{ message: string; id: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.post(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/timesheetEntry`,
        params,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return res.data;
    },
    onError: () => {
      snackbarCtx.showSnackbar("Error creating time entry", "error");
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["timesheet"]);
    },
  });
};

export const useUpdateTimeEntry = () => {
  const app = useRealmApp();
  const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();
  return useMutation({
    mutationFn: async (update: {
      entries: {
        _id: string;
        time?: number;
        isBillable?: boolean;
        status?: string;
        // categoryId?: string;
        description?: string;
      }[];
    }): Promise<{ message: string; id: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.post(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/updateTimesheetEntries`,
        update,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return res.data;
    },
    onError: () => {
      snackbarCtx.showSnackbar("Error updating time entry", "error");
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["timesheet"]);
    },
  });
};

export const useSubmitTimesheetApproval = () => {
  const navigate = useNavigate();
  const app = useRealmApp();
  const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();
  return useMutation({
    mutationFn: async (update: {
      timesheetId: string;
    }): Promise<{ message: string; id: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.post(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/submitTimesheetApproval`,
        update,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return res.data;
    },
    onError: () => {
      snackbarCtx.showSnackbar("Error submitting timesheet approval", "error");
    },
    onSuccess: () => {
      snackbarCtx.showSnackbar("Timesheet approval submitted!", "success");
      navigate("/time-tracking/approvals");
      queryClient.invalidateQueries(["timesheet"]);
    },
  });
};

export const useUpdateTimesheet = () => {
  const app = useRealmApp();
  const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();
  return useMutation({
    mutationFn: async (update: {
      _id: string;
      isSubmitted?: boolean;
    }): Promise<{ message: string; id: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.post(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/updateTimesheet`,
        update,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return res.data;
    },
    onError: () => {
      snackbarCtx.showSnackbar("Error submitting timesheet", "error");
    },
    onSuccess: () => {
      snackbarCtx.showSnackbar("Timesheet submitted!", "success");
      queryClient.invalidateQueries(["timesheet"]);
    },
  });
};

export const useCreateTimeEntries = () => {
  const app = useRealmApp();
  const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();
  return useMutation({
    mutationFn: async (params: {
      entries: TimeEntryRequestParams[];
    }): Promise<{ message: string; id: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.post(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/timesheetEntries`,
        params,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return res.data;
    },
    onError: () => {
      snackbarCtx.showSnackbar("Error creating time entries", "error");
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["timesheet"]);
    },
  });
};

export const useCreateTimesheetRow = (week: string) => {
  const app = useRealmApp();
  // const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();
  return useMutation({
    mutationFn: async (timesheetRow: {
      phaseId: string;
      item?: TimesheetItemData;
    }): Promise<{ message: string; id: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.post(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/timesheetRow`,
        {
          week,
          ...timesheetRow,
        },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return res.data;
    },
    onError: () => {
      // snackbarCtx.showSnackbar("Error creating space", "error");
    },
    onSuccess: (data, variables) => {
      // navigate(`/spaces/${data.id}/tasks`);
      // queryClient.invalidateQueries(spacesKeys.spaces());
      // queryClient.invalidateQueries(
      //   statusGroupKeys.statusGroups(
      //     app.currentUser?.customData?.instanceId?.$oid
      //   )
      // );
      queryClient.invalidateQueries(
        timesheetsKeys.timesheet(week, app.currentUser?.customData._id)
      );
      queryClient.invalidateQueries(phaseKeys.phase(variables.phaseId));
    },
  });
};

export const useUpdateTimesheetRow = (week: string) => {
  const app = useRealmApp();
  // const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();
  return useMutation({
    mutationFn: async ({
      timesheetRowUpdate,
    }: {
      timesheetRowUpdate: {
        _id: string;
        item: TimesheetItemData;
      };
    }): Promise<{ message: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.post(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/updateTimesheetRow`,
        timesheetRowUpdate,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return res.data;
    },
    onError: () => {
      // snackbarCtx.showSnackbar("Error creating space", "error");
    },
    onSuccess: (data, variables) => {
      // navigate(`/spaces/${data.id}/tasks`);
      // queryClient.invalidateQueries(spacesKeys.spaces());
      // queryClient.invalidateQueries(
      //   statusGroupKeys.statusGroups(
      //     app.currentUser?.customData?.instanceId?.$oid
      //   )
      // );
      queryClient.invalidateQueries(
        timesheetsKeys.timesheet(week, app.currentUser?.customData._id)
      );
    },
  });
};

export const useCreateTimesheetEntry = (week: string) => {
  const app = useRealmApp();
  // const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();
  return useMutation({
    mutationFn: async (params: {
      timesheetEntry: {
        rowId: string;
        date: string;
        time: number;
        isBillable: boolean;
        categoryId?: string;
        description: string;
      };
      phaseId: string;
    }): Promise<{ message: string; id: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.post(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/timesheetEntry`,
        params.timesheetEntry,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return res.data;
    },
    onError: () => {
      // snackbarCtx.showSnackbar("Error creating space", "error");
    },
    onSuccess: (data, variables) => {
      // navigate(`/spaces/${data.id}/tasks`);
      // queryClient.invalidateQueries(spacesKeys.spaces());
      // queryClient.invalidateQueries(
      //   statusGroupKeys.statusGroups(
      //     app.currentUser?.customData?.instanceId?.$oid
      //   )
      // );
      // console.log(
      //   timesheetsKeys.timesheet(week, app.currentUser?.customData._id)
      // );
      queryClient.invalidateQueries(
        timesheetsKeys.timesheet(week, app.currentUser?.customData._id)
      );
      queryClient.invalidateQueries(phaseKeys.phase(variables.phaseId));
    },
  });
};

export const useCreateTimesheetEntryOld = (week: string) => {
  const app = useRealmApp();
  // const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();
  return useMutation({
    mutationFn: async (params: {
      timesheetEntry: {
        rowId: string;
        date: string;
        time: number;
        isBillable: boolean;
        categoryId?: string;
        description: string;
      };
      phaseId: string;
    }): Promise<{ message: string; id: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.post(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/timesheetEntryOld`,
        params.timesheetEntry,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return res.data;
    },
    onError: () => {
      // snackbarCtx.showSnackbar("Error creating space", "error");
    },
    onSuccess: (data, variables) => {
      // navigate(`/spaces/${data.id}/tasks`);
      // queryClient.invalidateQueries(spacesKeys.spaces());
      // queryClient.invalidateQueries(
      //   statusGroupKeys.statusGroups(
      //     app.currentUser?.customData?.instanceId?.$oid
      //   )
      // );
      // console.log(
      //   timesheetsKeys.timesheet(week, app.currentUser?.customData._id)
      // );
      queryClient.invalidateQueries(
        timesheetsKeys.timesheet(week, app.currentUser?.customData._id)
      );
      queryClient.invalidateQueries(phaseKeys.phase(variables.phaseId));
    },
  });
};

export const useUpdateTimesheetEntry = (week: string) => {
  const app = useRealmApp();
  // const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();
  return useMutation({
    mutationFn: async (timesheetEntry: {
      _id: string;
      time?: number;
      isBillable?: boolean;
      categoryId?: string;
      description?: string;
    }): Promise<{ message: string; id: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.post(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/updateTimesheetEntry`,
        timesheetEntry,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return res.data;
    },
    onError: () => {
      // snackbarCtx.showSnackbar("Error creating space", "error");
    },
    onSuccess: (data) => {
      // navigate(`/spaces/${data.id}/tasks`);
      // queryClient.invalidateQueries(spacesKeys.spaces());
      // queryClient.invalidateQueries(
      //   statusGroupKeys.statusGroups(
      //     app.currentUser?.customData?.instanceId?.$oid
      //   )
      // );
      // console.log(
      //   timesheetsKeys.timesheet(week, app.currentUser?.customData._id)
      // );
      queryClient.invalidateQueries(
        timesheetsKeys.timesheet(week, app.currentUser?.customData._id)
      );
    },
  });
};

export const useUpdateTimesheetEntryOld = (week: string) => {
  const app = useRealmApp();
  // const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();
  return useMutation({
    mutationFn: async (timesheetEntry: {
      _id: string;
      time?: number;
      isBillable?: boolean;
      categoryId?: string;
      description?: string;
    }): Promise<{ message: string; id: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.post(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/updateTimesheetEntryOld`,
        timesheetEntry,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return res.data;
    },
    onError: () => {
      // snackbarCtx.showSnackbar("Error creating space", "error");
    },
    onSuccess: (data) => {
      // navigate(`/spaces/${data.id}/tasks`);
      // queryClient.invalidateQueries(spacesKeys.spaces());
      // queryClient.invalidateQueries(
      //   statusGroupKeys.statusGroups(
      //     app.currentUser?.customData?.instanceId?.$oid
      //   )
      // );
      // console.log(
      //   timesheetsKeys.timesheet(week, app.currentUser?.customData._id)
      // );
      queryClient.invalidateQueries(
        timesheetsKeys.timesheet(week, app.currentUser?.customData._id)
      );
    },
  });
};

export const useDeleteTimesheetRow = () => {
  const app = useRealmApp();
  const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();

  return useMutation({
    mutationFn: async (timesheetRow: {
      _id: string;
    }): Promise<{ message: string; id: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.delete(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/timesheetRow`,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          params: timesheetRow,
        }
      );
      return res.data;
    },
    onError: () => {
      snackbarCtx.showSnackbar("Error deleting timesheet row", "error");
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["timesheet"]);
      // if (data.id && variables.sharedSpaceId) {
      //   queryClient.invalidateQueries(
      //     phaseKeys.phases(variables.sharedSpaceId)
      //   );
      // }
      // snackbarCtx.showSnackbar("Successfully duplicated journey template");
    },
  });
};

export const useDeleteTimesheetEntry = () => {
  const app = useRealmApp();
  const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();

  return useMutation({
    mutationFn: async (timesheetEntry: {
      _id: string;
    }): Promise<{ message: string; id: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.delete(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/timesheetEntry`,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          params: timesheetEntry,
        }
      );
      return res.data;
    },
    onError: () => {
      snackbarCtx.showSnackbar("Error deleting timesheet entry", "error");
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["timesheet"]);
      queryClient.invalidateQueries(["entries"]);
    },
  });
};
